/**
 * PKCE (Proof Key for Code Exchange) Utilities
 *
 * Provides utilities for implementing OAuth 2.0 PKCE flow with Facebook OAuth.
 * PKCE adds security to the authorization code flow by using a dynamically
 * generated secret to ensure the client that started the flow is the same
 * client that exchanges the authorization code for tokens.
 */

/**
 * Generate a cryptographically secure random string for use as code verifier
 * @param length Length of the code verifier (43-128 characters per RFC 7636)
 * @returns Base64url-encoded random string
 */
export function generateCodeVerifier(length: number = 128): string {
  if (length < 43 || length > 128) {
    throw new Error('Code verifier length must be between 43 and 128 characters');
  }

  // Generate random bytes
  const array = new Uint8Array(Math.ceil((length * 3) / 4));
  crypto.getRandomValues(array);

  // Convert to base64url encoding (RFC 4648)
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
    .substring(0, length);
}

/**
 * Generate SHA256 code challenge from code verifier
 * @param codeVerifier The code verifier string
 * @returns Base64url-encoded SHA256 hash of the code verifier
 */
export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  // Encode the code verifier as UTF-8
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);

  // Hash with SHA256
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = new Uint8Array(hashBuffer);

  // Convert to base64url encoding
  return btoa(String.fromCharCode(...hashArray))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * PKCE parameters for OAuth flow
 */
export interface PKCEParams {
  codeVerifier: string;
  codeChallenge: string;
  state: string;
}

/**
 * Generate complete PKCE parameters for OAuth flow
 * @param state Optional state parameter for CSRF protection
 * @returns Complete PKCE parameters
 */
export async function generatePKCEParams(state?: string): Promise<PKCEParams> {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Generate state if not provided (for CSRF protection)
  const finalState = state || generateCodeVerifier(32);

  return {
    codeVerifier,
    codeChallenge,
    state: finalState,
  };
}

/**
 * Session storage keys for PKCE parameters
 */
const STORAGE_KEYS = {
  CODE_VERIFIER: 'oauth_code_verifier',
  STATE: 'oauth_state',
} as const;

/**
 * Store PKCE parameters in session storage
 * @param params PKCE parameters to store
 */
export function storePKCEParams(params: Pick<PKCEParams, 'codeVerifier' | 'state'>): void {
  if (typeof window === 'undefined') {
    console.warn('storePKCEParams called in non-browser environment');
    return;
  }

  try {
    sessionStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, params.codeVerifier);
    sessionStorage.setItem(STORAGE_KEYS.STATE, params.state);
  } catch (error) {
    console.error('Failed to store PKCE parameters:', error);
    throw new Error(
      'Failed to store OAuth parameters. Please ensure session storage is available.'
    );
  }
}

/**
 * Retrieve PKCE parameters from session storage
 * @returns Stored PKCE parameters or null if not found
 */
export function retrievePKCEParams(): Pick<PKCEParams, 'codeVerifier' | 'state'> | null {
  if (typeof window === 'undefined') {
    console.warn('retrievePKCEParams called in non-browser environment');
    return null;
  }

  try {
    const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.CODE_VERIFIER);
    const state = sessionStorage.getItem(STORAGE_KEYS.STATE);

    if (!codeVerifier || !state) {
      return null;
    }

    return { codeVerifier, state };
  } catch (error) {
    console.error('Failed to retrieve PKCE parameters:', error);
    return null;
  }
}

/**
 * Clear stored PKCE parameters from session storage
 */
export function clearPKCEParams(): void {
  if (typeof window === 'undefined') {
    console.warn('clearPKCEParams called in non-browser environment');
    return;
  }

  try {
    sessionStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
    sessionStorage.removeItem(STORAGE_KEYS.STATE);
  } catch (error) {
    console.error('Failed to clear PKCE parameters:', error);
  }
}

/**
 * Verify that the returned state matches the stored state (CSRF protection)
 * @param returnedState State parameter returned from OAuth provider
 * @returns True if state is valid, false otherwise
 */
export function verifyState(returnedState: string): boolean {
  const storedParams = retrievePKCEParams();

  if (!storedParams) {
    console.error('No stored OAuth state found');
    return false;
  }

  const isValid = storedParams.state === returnedState;

  if (!isValid) {
    console.error('OAuth state mismatch - possible CSRF attack');
  }

  return isValid;
}
